<script setup lang="ts">
    import { DatePicker } from '@/components/ui/form';
    import { ref } from 'vue';
    import { toast } from 'vue-sonner';
    import { useDateFormat } from '@/composables/useDateFormat';
    import { useI18n } from 'vue-i18n';
    import { useUpdateDatatableRow } from '@/composables/useUpdateDatatableRow';
    import axios from 'axios';
    import QueryKey from '@/enums/QueryKey';
    import type { DashboardTableData } from '@/types';
    import type { Row } from '@tanstack/vue-table';

    const { formatDMYFromIso } = useDateFormat();

    const { updateDataTableRow } = useUpdateDatatableRow();

    const props = defineProps<{
        row: Row<DashboardTableData>;
        reportingPeriod: string;
        selectedGrouping: string;
        columnId: keyof DashboardTableData;
    }>();

    const { t } = useI18n();

    const currentValue = ref(props.row.original[props.columnId] as string);

    const initialized = ref(false);

    const handleDateChange = async (newValue: string) => {
        if (!initialized.value) return;

        const previousValue = currentValue.value;

        try {
            if (newValue === currentValue.value) return;

            currentValue.value = newValue;

            await axios.post(
                route('v2.dashboard.pbc-deadline.update', { company: props.row.original.id }),
                {
                    reporting_period: props.reportingPeriod,
                    deadline: newValue,
                },
            );

            updateDataTableRow(
                [
                    QueryKey.EMPLOYEE_DASHBOARD,
                    {
                        selectedGrouping: props.selectedGrouping,
                        reportingPeriod: props.reportingPeriod,
                    },
                ],
                props.row.index,
                props.columnId,
                newValue,
            );
        } catch (error) {
            currentValue.value = previousValue;
            console.error('Failed to update status:', error);
            toast.error(t('dashboards.status_update_failed'));
        }
    };
</script>

<template>
    <div class="flex items-center justify-between gap-2">
        <span class="text-xs">
            {{ currentValue ? formatDMYFromIso(currentValue) : '' }}
        </span>
        <DatePicker
            :model-value="currentValue"
            @update:model-value="handleDateChange"
            class="ml-auto border-none px-0"
            :show-input="false"
            @click="initialized = true"
        />
    </div>
</template>
